import React from 'react';
import { INPUT_STEPS } from '../constants';

type FieldType = 'number' | 'currency' | 'text' | 'textarea' | 'select';

type SelectOption = {
    value: string | number;
    label: string;
};

interface EditableFieldProps {
    label: string;
    value: string | number;
    isEditing: boolean;
    hasError: boolean;
    fieldType: FieldType;
    onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;
    ariaLabel: string;
    options?: SelectOption[];
    disableWheel?: boolean;
}

/**
 * Generic editable field component for use in forms throughout the application.
 * 
 * Supports multiple field types (number, currency, text, textarea) with:
 * - View/Edit mode toggling
 * - Validation error styling
 * - Accessibility (ARIA attributes)
 * - Consistent formatting (e.g., currency with 2 decimals)
 * 
 * **Styling:** Uses `.editable-field-label` class (currently defined in payroll.style.scss)
 * When moving to common/, extract styles to a shared stylesheet.
 */

export const EditableField: React.FC<EditableFieldProps> = React.memo(({
    label,
    value,
    isEditing,
    hasError,
    fieldType,
    onChange,
    ariaLabel,
    options,
    disableWheel = false,
}) => {
    const displayValue = () => {
        if (fieldType === 'currency') {
            return typeof value === 'number' ? value.toFixed(2) : '';
        }
        if (fieldType === 'select' && options?.length) {
            const selected = options.find(option => option.value === value);
            return selected?.label ?? value ?? '';
        }
        return value ?? '';
    };

    const renderInput = () => {
        if (fieldType === 'textarea') {
            return (
                <textarea
                    className="form-control timesheet-textarea"
                    value={value || ''}
                    onChange={onChange}
                    aria-label={ariaLabel}
                    aria-invalid={hasError}
                />
            );
        }

        if (fieldType === 'select') {
            return (
                <select
                    className="form-control"
                    value={value ?? ''}
                    onChange={onChange}
                    aria-label={ariaLabel}
                    aria-invalid={hasError}
                >
                    <option value="">Select...</option>
                    {options?.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            );
        }

        const inputType = fieldType === 'text' ? 'text' : 'number';
        const step = fieldType === 'currency' ? INPUT_STEPS.CURRENCY : INPUT_STEPS.DURATION;

        const wheelHandler =
            disableWheel && inputType === 'number'
                ? (e: React.WheelEvent<HTMLInputElement>) => {
                    e.preventDefault();
                    e.currentTarget.blur();
                }
                : undefined;

        return (
            <input
                className="form-control"
                type={inputType}
                min={inputType === 'number' ? '0' : undefined}
                step={inputType === 'number' ? step : undefined}
                value={value ?? ''}
                onChange={onChange}
                aria-label={ariaLabel}
                aria-invalid={hasError}
                onWheel={wheelHandler}
                onWheelCapture={wheelHandler}
            />
        );
    };

    return (
        <tr>
            <td className="editable-field-label">{label}</td>
            <td className={hasError && !isEditing ? 'has-error' : ''}>
                {isEditing ? renderInput() : displayValue()}
            </td>
        </tr>
    );
});

EditableField.displayName = 'EditableField';

